"use client";

import { useGlobalStore } from "@/store/store";
import Button from "../ui/button/Button";
import useCRouter from "@/hooks/useCRouter";
import { sessionFlowConstants, sessionQueryParams } from "@/apiUtils/constants";
import useSearchParams from "@/hooks/useSearchParams";
import { useEffect, useMemo, useRef, useState } from "react";
import SignInSignUpModal from "../MagrabiComponents/SignInSignUpModal";
import { createPortal } from "react-dom";
import { isRedirectDisabled } from "@/apiUtils/commonUtils";
import { useIsClient } from "@/hooks/useIsClient";

interface IProps {
  translation: Record<string, string>;
}

export const NavReorderLenses = ({ translation }: IProps) => {
  // hooks
  const { isAuthenticated, user } = useGlobalStore();
  const router = useCRouter();
  const { updateSearchParam, searchParams } = useSearchParams();
  const isClient = useIsClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { sessionInfoM, sessionInfoTabs, isCheckout } = useMemo(() => {
    return {
      sessionInfoM: searchParams.get(sessionQueryParams.LOGIN_MODAL_STATUS),
      sessionInfoTabs: [
        sessionFlowConstants.LOGIN_EMAIL,
        sessionFlowConstants.LOGIN_PHONE,
        sessionFlowConstants.REGISTER,
        sessionFlowConstants.GUEST,
      ],
      isCheckout: searchParams.get(sessionQueryParams.IS_CHECKOUT),
    };
  }, [searchParams]);

  // states
  const [activeTab, setActiveTab] = useState<string>(sessionInfoM || sessionInfoTabs[0]);
  const [loadedOnce, setLoadedOnce] = useState<boolean>(false);

  // consts
  const show = useMemo(() => {
    if (sessionInfoM) {
      setActiveTab(sessionInfoM);
      return sessionInfoTabs.includes(sessionInfoM);
    }
    return false;
  }, [sessionInfoM, sessionInfoTabs]);

  function handleSignInOrSignUp(tab?: string) {
    if (isAuthenticated) {
      router.push("/my-account/dashboard");
      return;
    }

    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: activeTab || tab || sessionInfoTabs[0] },
    ]);
  }

  function handleCloseAuthDrawer() {
    setActiveTab(sessionInfoM || sessionInfoTabs[0]);
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: undefined },
      { key: sessionQueryParams.IS_CHECKOUT, value: undefined },
    ], true);
  }

  // lifecycles
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoadedOnce(true);
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Button
        className="flex h-[33px] w-[170px] items-center justify-center border-0 p-0 font-semibold md:p-0"
        onClick={() => {
          if (!user) handleSignInOrSignUp(activeTab);
          else {
            router.push("/my-account/orders?filter=reorder");
          }
        }}
      >
        <span className="text-xs">{translation?.header_button_reorder_cl}</span>
      </Button>

      {loadedOnce && !isAuthenticated && isClient
        ? createPortal(
            <SignInSignUpModal
              show={!!show}
              translations={{}}
              activeTab={activeTab}
              tabs={sessionInfoTabs}
              onLoaded={() => {}}
              isCheckoutFlow={!!isCheckout}
              onClose={handleCloseAuthDrawer}
              disableRedirect={isRedirectDisabled(searchParams)}
            />,
            document.body,
            "key-session",
          )
        : null}
    </>
  );
};
